import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../Context/UserContext.js";
import { createRoute } from "../routing/createRoute.js";
import {
  isQuickCreateEnabled,
  isTypingInto,
  runQuickCreate136,
} from "./quickCreate136.js";

/**
 * Hook that listens for Cmd+Shift+O (Mac) / Ctrl+Shift+O (Windows/Linux)
 * on the home page and creates a fully populated 136 incident with stub data,
 * fabricating the person and the place of safety.
 *
 * Only active in dev/staging environments - does nothing in production.
 *
 * See `QuickCreateDemoIncident` for the variant that asks who the person is and
 * which place of safety they were taken to.
 */
export function useQuickCreate136() {
  const navigate = useNavigate();
  const { user } = useUser();
  const isRunningRef = useRef(false);

  const createIncident = useCallback(async () => {
    if (!user) {
      console.warn("Quick create 136: no user available");
      return;
    }
    if (isRunningRef.current) {
      console.warn("Quick create 136: already in progress");
      return;
    }
    isRunningRef.current = true;

    try {
      const incidentWorkItemId = await runQuickCreate136({ user });
      navigate(createRoute.incidentProgressPage(incidentWorkItemId));
    } catch (error) {
      console.error("Quick create 136 failed:", error);
    } finally {
      isRunningRef.current = false;
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!isQuickCreateEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === "o"
      ) {
        if (isTypingInto(document.activeElement)) return;
        e.preventDefault();
        void createIncident();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [createIncident]);
}
